import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { CompanyTabListParams, PaginatedResponse } from '../../../interfaces/companyDetail.interface';

export interface SelectableOption {
  id: number;
  name: string;
}

type OptionFetcher = (
  companyId: number | string,
  params?: CompanyTabListParams,
) => Promise<PaginatedResponse<{ id?: number; name: string }>>;

type AsyncCompanyMultiSelectProps = {
  companyId?: number;
  // Paginated, server-search-capable endpoint (e.g. getCompanyServices / getCompanySites).
  fetcher: OptionFetcher;
  // Currently selected ids (controlled by the parent form).
  selectedOptions: number[];
  optionsCallback: (ids: number[]) => void;
  // The already-selected objects (id + name), so their chips render without a fetch.
  initialOptions?: { id?: number; name?: string }[];
  inputLabel: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  // How many options to request per search (bounded — never a full dump).
  pageSize?: number;
};

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;

/**
 * Searchable, server-paginated multi-select for company-scoped reference data
 * (services, sites). Fetches a bounded page of options matching the typed query
 * instead of loading the company's entire list, so it never needs limit=9999.
 * Already-selected items keep their labels via `initialOptions` + a cache of
 * everything seen, so chips render even when not in the current search results.
 */
const AsyncCompanyMultiSelect = (props: AsyncCompanyMultiSelectProps) => {
  const { companyId, fetcher, selectedOptions, optionsCallback, initialOptions, inputLabel, disabled, size, pageSize } = props;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SelectableOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Label cache for every option ever seen (selected + searched), so selected
  // chips always have a name even if absent from the latest search page.
  const knownById = useRef<Map<number, SelectableOption>>(new Map());
  useEffect(() => {
    (initialOptions ?? []).forEach((opt) => {
      if (opt.id != null) knownById.current.set(opt.id, { id: opt.id, name: opt.name ?? '' });
    });
  }, [initialOptions]);

  const runSearch = useCallback(
    (search: string) => {
      if (companyId == null) return;
      setLoading(true);
      fetcher(companyId, { search, limit: pageSize ?? DEFAULT_PAGE_SIZE, offset: 0 })
        .then((response) => {
          const fetched = response.results
            .filter((r): r is SelectableOption => r.id != null)
            .map((r) => ({ id: r.id as number, name: r.name }));
          fetched.forEach((opt) => knownById.current.set(opt.id, opt));
          setOptions(fetched);
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    },
    [companyId, fetcher, pageSize],
  );

  // Debounced search as the user types; also runs an initial (empty) search on open.
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => runSearch(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, inputValue, runSearch]);

  const value: SelectableOption[] = useMemo(
    () => selectedOptions.map((id) => knownById.current.get(id) ?? { id, name: `#${id}` }),
    [selectedOptions, options],
  );

  // Merge selected items into the option list so MUI never sees an out-of-range
  // value (selected items may not be in the current search page).
  const mergedOptions: SelectableOption[] = useMemo(() => {
    const byId = new Map<number, SelectableOption>();
    value.forEach((opt) => byId.set(opt.id, opt));
    options.forEach((opt) => byId.set(opt.id, opt));
    return Array.from(byId.values());
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      sx={{ mt: '.5rem' }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disabled={disabled}
      size={size}
      options={mergedOptions}
      value={value}
      loading={loading}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => option.name}
      inputValue={inputValue}
      onInputChange={(_e, newInput, reason) => {
        if (reason !== 'reset') setInputValue(newInput);
      }}
      onChange={(_e, newValue) => {
        newValue.forEach((opt) => knownById.current.set(opt.id, opt));
        optionsCallback(newValue.map((opt) => opt.id));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color='inherit' size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AsyncCompanyMultiSelect;
